import { digitalCartFetch } from '@theme/do3d-digital-quantity';
import { CartLinesUpdateEvent } from '@shopify/events';

export class Do3DCartClear extends HTMLElement {
  static pending = false;

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
  }

  onKeydown = (event) => {
    if (event.key === 'Escape' && !Do3DCartClear.pending && !this.querySelector('.do3d-cart-clear__confirmation').hidden) {
      event.preventDefault();
      event.stopPropagation();
      this.closeConfirmation();
    }
  };

  onClick = (event) => {
    if (!(event.target instanceof Element) || Do3DCartClear.pending) return;
    if (event.target.closest('.do3d-cart-clear__open')) {
      this.querySelector('.do3d-cart-clear__confirmation').hidden = false;
      this.querySelector('.do3d-cart-clear__open').setAttribute('aria-expanded', 'true');
      this.querySelector('[data-cart-clear-cancel]').focus();
    } else if (event.target.closest('[data-cart-clear-cancel]')) {
      this.closeConfirmation();
    } else if (event.target.closest('[data-cart-clear-confirm]')) {
      this.clearCart();
    }
  };

  closeConfirmation() {
    this.querySelector('.do3d-cart-clear__confirmation').hidden = true;
    this.querySelector('.do3d-cart-clear__open').setAttribute('aria-expanded', 'false');
    this.querySelector('.do3d-cart-clear__open').focus();
  }

  async clearCart() {
    if (Do3DCartClear.pending) return;
    Do3DCartClear.pending = true;
    const error = this.querySelector('.do3d-cart-clear__error');
    error.hidden = true;
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-cart-clear-label]').hidden = true;
    this.querySelector('[data-cart-clear-pending]').hidden = false;
    const components = [...document.querySelectorAll('cart-items-component')];
    const inertStates = components.map(component => component.inert);
    const buttons = [...document.querySelectorAll('do3d-cart-clear button')];
    buttons.forEach(button => { button.disabled = true; });
    components.forEach(component => {
      component.cancelPendingQuantityChange?.();
      component.inert = true;
    });
    const sectionIds = [...new Set(components.map(component => component.dataset.sectionId).filter(Boolean))].slice(0, 5);
    let cleared = false;
    try {
      const response = await digitalCartFetch(`${window.Shopify.routes.root}cart/clear.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ sections: sectionIds, sections_url: window.location.pathname + window.location.search })
      });
      const cart = await response.json();
      if (!response.ok || cart.errors || cart.status || cart.item_count !== 0) throw new Error(this.dataset.error);
      cleared = true;
      if (sectionIds.some(id => typeof cart.sections?.[id] !== 'string')) {
        window.location.reload();
        return;
      }
      document.querySelectorAll('quantity-selector-component input[data-cart-quantity]').forEach(input => {
        input.setAttribute('data-cart-quantity', '0');
        input.closest('quantity-selector-component')?.updateCartQuantity?.();
      });
      const update = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({ action: 'remove', context: 'cart', lines: [], promise: update.promise }));
      update.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { sections: cart.sections, items: [], itemCount: 0, source: 'do3d-cart-clear', didError: false }
      });
    } catch (cause) {
      error.textContent = this.dataset.error;
      error.hidden = false;
    } finally {
      components.forEach((component, index) => { component.inert = inertStates[index]; });
      buttons.forEach(button => { button.disabled = false; });
      this.removeAttribute('aria-busy');
      this.querySelector('[data-cart-clear-label]').hidden = false;
      this.querySelector('[data-cart-clear-pending]').hidden = true;
      Do3DCartClear.pending = false;
      if (!cleared) this.querySelector('[data-cart-clear-cancel]').focus();
    }
  }
}

if (!customElements.get('do3d-cart-clear')) customElements.define('do3d-cart-clear', Do3DCartClear);
